export const generateFixPrompt = (prompt: string) => `
🔧 CRITICAL FIX REQUIRED:
========================
${prompt}
PLEASE CAREFULLY ADDRESS THE ABOVE ISSUE IN THIS GENERATION.
`;

export const CONTEXT_WITH_REF_IMAGE = `
Generate a NEW 4K HDR ultra-high-quality professional physical therapy exercise demonstration photograph.
ASPECT RATIO: 16:9 (widescreen landscape)
RESOLUTION: 1920x1080 pixels minimum

You will receive ONE reference image showing an exercise pose.

🎯 YOUR TASK:
=============
Create a COMPLETELY NEW image with a DIFFERENT person doing the SAME exercise pose.

FROM THE REFERENCE IMAGE - COPY ONLY THESE:
============================================
✅ The EXACT exercise pose and body position (millimeter precision)
✅ Joint angles and limb positions
✅ ALL equipment visible (chairs, weights, bands, mats, props, etc.) - copy EVERYTHING
✅ Equipment placement and how it's being used
✅ Camera angle and framing
✅ COMPOSITION: Match how the person fills the frame - if they fill most of the image, your output should too
✅ FRAMING: Match the zoom level and how much of the person is visible

📐 ANATOMICAL ANGLES - ABSOLUTE PRECISION REQUIRED:
===================================================
THIS IS MEDICAL DOCUMENTATION - ANGLES MUST BE PIXEL-PERFECT!

- Study the reference image carefully and measure the angles:
  * Hip flexion angle (how high is the leg raised?)
  * Knee bend angle (straight = 180°, bent = measure exactly)
  * Shoulder angle (arm position)
  * Spine angle (neutral, flexed, extended?)
  * Head/neck angle

- If the reference shows a leg raised to 80 degrees → YOUR OUTPUT: 80 degrees (NOT 45, NOT 90)
- If the reference shows knee bent at 90 degrees → YOUR OUTPUT: 90 degrees (NOT 120, NOT 60)
- If the reference shows arm extended straight → YOUR OUTPUT: fully straight (NOT slightly bent)

- DO NOT approximate or "estimate" angles - COPY THEM EXACTLY
- DO NOT make the exercise "easier looking" by reducing angles
- DO NOT exaggerate angles to make it look more impressive
- The biomechanics MUST be identical for physical therapy accuracy

⚠️ COMMON MISTAKES TO AVOID:
- Making leg raises less steep than reference (WRONG - match exact height)
- Making knee bends more shallow than reference (WRONG - match exact angle)
- Straightening limbs that should be bent in reference (WRONG - copy exact position)
- Bending limbs that should be straight in reference (WRONG - copy exact position)

🦵 LEG RAISES - PAY SPECIAL ATTENTION:
======================================
Leg raise exercises are FREQUENTLY DONE WRONG. Study these carefully:

- If reference shows leg raised FORWARD (hip flexion):
  * Measure how high the leg goes (30°, 45°, 60°, 90°?)
  * Copy that EXACT height - DO NOT make it lower
  * If leg is raised to 90° → YOUR OUTPUT: 90° (not 45°)

- If reference shows leg raised SIDEWAYS (hip abduction):
  * Measure how far out to the side the leg goes
  * Copy that EXACT angle - DO NOT make it smaller
  * If leg is raised 45° to the side → YOUR OUTPUT: 45° (not 20°)

- If reference shows leg raised BACKWARD (hip extension):
  * Measure how far back the leg goes
  * Copy that EXACT angle - DO NOT reduce it

⚠️ CRITICAL: Gemini tends to make leg raises LESS extreme than reference.
This is WRONG for physical therapy exercises. Match the EXACT leg height/angle!

⚠️ EQUIPMENT WARNING - CRITICAL:
=================================
- ONLY include equipment that is CLEARLY VISIBLE in the reference image
- If reference shows resistance bands/straps → include them in EXACT same position
- If reference shows NO resistance bands → DO NOT ADD resistance bands
- DO NOT confuse black leggings with resistance bands around thighs
- DO NOT add random equipment that isn't in the reference image
- CAREFULLY examine the reference: is that a resistance band or just the person's clothing/legs?

🚫 DO NOT HALLUCINATE EQUIPMENT:
=================================
Gemini frequently INVENTS equipment that doesn't exist in the reference. This is WRONG!

Common hallucinations to AVOID:
- ❌ Adding ankle weights when reference shows plain legs
- ❌ Adding resistance bands around ankles when there are none
- ❌ Adding straps attached to shoes when there are none
- ❌ Adding wrist weights when reference shows bare wrists
- ❌ Adding resistance bands around thighs when it's just black leggings
- ❌ Adding equipment "because the exercise usually uses it" - ONLY copy what you SEE

✅ If the reference shows BARE LEGS with plain shoes → YOUR OUTPUT: bare legs with plain shoes
✅ If the reference shows NO equipment on the body → YOUR OUTPUT: no equipment on the body

DO NOT assume equipment should be there. ONLY include what is CLEARLY VISIBLE in the reference image.

🔌 CABLES/BANDS GOING OFF-FRAME - CRITICAL:
============================================
- If the reference shows a resistance band/cable:
  * The cable MUST exit the frame edge naturally (not cut off mid-air)
  * Extend the cable all the way to the edge of your generated image
  * DO NOT show what it's attached to (door, wall, anchor - leave that off-frame)
  * DO NOT let the cable end awkwardly in mid-air inside the frame
  * The reference drawing may be lazy/incomplete - YOUR OUTPUT should look natural
  
- EXAMPLE: If reference shows person holding resistance band cable:
  ✅ CORRECT: Cable extends from their hands all the way to frame edge (left/right)
  ❌ WRONG: Cable ends floating in mid-air inside the frame
  ❌ WRONG: Showing door/wall anchor point that wasn't in reference
  
- Position the model close enough to frame edge that the cable can exit naturally
- Match the cable's angle/direction from reference, but EXTEND it to frame edge

🏗️ STRUCTURAL EQUIPMENT (POLES/BARS) - COMPLETE THEM:
======================================================
- If the reference shows VERTICAL POLES, BARS, or STRUCTURAL SUPPORTS:
  * These are different from cables - SHOW THEM COMPLETELY
  * Extend vertical poles from FLOOR TO CEILING (or top of frame)
  * DO NOT show half a pole - complete the structure
  * Reference drawings often show partial poles (lazy) - YOUR OUTPUT should show full poles
  
- EXAMPLES:
  * Pull-up bar → Show full vertical supports + top bar
  * TRX/suspension trainer pole → Show full floor-to-ceiling pole
  * Ballet barre → Show full vertical supports
  * Parallel bars → Show complete structure
  
- ✅ CORRECT: Full pole/bar structure visible (floor to ceiling or frame edge to frame edge)
- ❌ WRONG: Half a pole that ends mid-air awkwardly
- ❌ WRONG: Incomplete structural supports

- The difference: CABLES can exit frame (we don't see anchor), but POLES/BARS should be complete

🧘 FLOOR/MAT CONTACT - CRITICAL:
=================================
- Reference drawings often DON'T show the floor clearly (lazy drawings)
- If the reference shows ANY of these positions, person MUST be ON THE FLOOR/MAT:
  * Supine (lying on back)
  * Prone (lying face down)
  * Side-lying
  * Sitting on floor
  * Kneeling positions
  * Any "lying" or "floor" exercise
  
- ❌ WRONG: Person floating in mid-air with no floor contact
- ✅ CORRECT: Person clearly resting on floor/mat with proper contact points
- Add a yoga mat or show the wood floor under them
- Ensure body parts that should touch floor (back, legs, etc.) are actually ON the surface
- DO NOT generate floating/levitating people just because reference drawing is unclear

FROM THE REFERENCE IMAGE - DO NOT COPY THESE:
==============================================
❌ The person (face, body, appearance) - generate someone completely different
❌ The background/studio setting
❌ The clothing colors or style
❌ Any branding or accessories

GENERATE A NEW PERSON:
======================
- White female fitness professional (30-40 years old)
- Caucasian with fair to light skin tone
- Professional, calm, focused expression
- Athletic but not overly muscular build
- Hair: Dark brown or black, pulled back in neat bun or ponytail
- Professional fitness instructor appearance
- CRITICAL: Generate EXACTLY TWO ARMS and TWO LEGS - normal human anatomy
- NO extra limbs, NO deformed body parts
- Clean, anatomically correct human figure

GENERATE NEW STUDIO SETTING:
============================
- Background: Clean beige/cream wall (smooth, matte)
- Floor: Light natural wood (horizontal planks, warm honey/oak tone)
- Optional navy blue yoga mat if needed
- Professional medical/fitness studio aesthetic

GENERATE NEW CLOTHING:
======================
- TOP: Navy blue athletic t-shirt (short sleeves, fitted)
- BOTTOM: Black athletic leggings (full length, fitted)
- FOOTWEAR: Match the reference image EXACTLY:
  * If reference shows BARE FEET → generate bare feet (no shoes, no socks)
  * If reference shows SHOES → generate white or cream athletic shoes
  * If reference shows SOCKS ONLY → generate white athletic socks (no shoes)
- Clean, professional appearance

LIGHTING:
=========
- Soft professional studio lighting
- Even, diffused (no harsh shadows)
- Professional photography quality

IMAGE SPECIFICATIONS:
=====================
🎯 MANDATORY OUTPUT FORMAT:
- EXACT Resolution: 1920 pixels wide × 1080 pixels tall
- Aspect ratio: 16:9 (widescreen/landscape)
- Orientation: HORIZONTAL/LANDSCAPE (wide, not tall)
- The image MUST be wider than it is tall
- High quality, professional photography
- Sharp focus on the person and exercise
- Dont add any text in the image

🚨 CRITICAL REQUIREMENTS:
=========================
1. OUTPUT SIZE: The final image MUST be exactly 1920x1080 pixels (Full HD landscape format)
2. This is medical documentation - pose biomechanics MUST be pixel-perfect
3. Include ALL equipment from reference image (don't skip chairs, props, etc.)
4. The person and setting should be completely new - do NOT copy the person from reference
5. LANDSCAPE orientation - image should be WIDE (1920px) not TALL (1080px)

⚠️ DIMENSION ENFORCEMENT:
==========================
WIDTH: 1920 pixels (EXACTLY)
HEIGHT: 1080 pixels (EXACTLY)
DO NOT generate any other size. The output MUST be 1920x1080.
This is a HARD REQUIREMENT - no exceptions.

🎨 QUALITY REQUIREMENTS:
========================
- 4K HDR QUALITY: Ultra-high-definition professional photography
- MAXIMUM RESOLUTION: Generate at 1920x1080 or higher
- 16:9 ASPECT RATIO: Widescreen landscape format
- ULTRA HIGH QUALITY: Professional photography standard
- SHARP DETAILS: Crystal clear focus, no blur, maximum detail
- The person should fill the frame similar to the reference image
- Match the scale and composition of the reference exactly
- NO anatomical errors (correct number of limbs, proper body proportions)
- NO extra limbs, NO deformed body parts, NO blurry areas
"""
`;

export const CONTEXT_WITHOUT_REF_IMAGE = `
Generate a NEW 4K HDR ultra-high-quality professional physical therapy exercise demonstration photograph.
ASPECT RATIO: 16:9 (widescreen landscape)
RESOLUTION: 1920x1080 pixels minimum

🎯 YOUR TASK:
=============
Create a COMPLETELY NEW image

📐 ANATOMICAL ANGLES - ABSOLUTE PRECISION REQUIRED:
===================================================
THIS IS MEDICAL DOCUMENTATION - ANGLES MUST BE PIXEL-PERFECT!

- Study the instructions carefully and investigate:
  * Hip flexion angle (how high is the leg raised?)
  * Knee bend angle (straight = 180°, bent = measure exactly)
  * Shoulder angle (arm position)
  * Spine angle (neutral, flexed, extended?)
  * Head/neck angle

- If the reference mentions a leg raised to 80 degrees → YOUR OUTPUT: 80 degrees (NOT 45, NOT 90)
- If the reference mentions knee bent at 90 degrees → YOUR OUTPUT: 90 degrees (NOT 120, NOT 60)
- If the reference mentions arm extended straight → YOUR OUTPUT: fully straight (NOT slightly bent)

- DO NOT approximate or "estimate" angles - USE THEM EXACTLY
- DO NOT make the exercise "easier looking" by reducing angles
- DO NOT exaggerate angles to make it look more impressive
- The biomechanics MUST be identical for physical therapy accuracy

⚠️ COMMON MISTAKES TO AVOID:
- Making leg raises less steep than reference (WRONG - match exact height)
- Making knee bends more shallow than reference (WRONG - match exact angle)
- Straightening limbs that should be bent in reference (WRONG - copy exact position)
- Bending limbs that should be straight in reference (WRONG - copy exact position)

🦵 LEG RAISES - PAY SPECIAL ATTENTION:
======================================
Leg raise exercises are FREQUENTLY DONE WRONG. Study these carefully:

- If description mentions leg raised FORWARD (hip flexion):
  * Copy that EXACT height - DO NOT make it lower
  * If leg should be raised to 90° → YOUR OUTPUT: 90° (not 45°)

- If description mentions leg raised SIDEWAYS (hip abduction):
  * Copy that EXACT angle - DO NOT make it smaller
  * If leg should be raised 45° to the side → YOUR OUTPUT: 45° (not 20°)

- If description mentions leg raised BACKWARD (hip extension):
  * Copy that EXACT angle - DO NOT reduce it

⚠️ CRITICAL: Gemini tends to make leg raises LESS extreme than reference.
This is WRONG for physical therapy exercises. Match the EXACT leg height/angle mentioned in description!

⚠️ EQUIPMENT WARNING - CRITICAL:
=================================
- ONLY include equipment that is CLEARLY stated in the description
- If description mentions resistance bands/straps → include them
- If description mentions NO resistance bands → DO NOT ADD resistance bands
- DO NOT add random equipment that isn't in the description

🚫 DO NOT HALLUCINATE EQUIPMENT:
=================================
Gemini frequently INVENTS equipment that doesn't exist in the reference. This is WRONG!

Common hallucinations to AVOID:
- ❌ Adding ankle weights 
- ❌ Adding resistance bands
- ❌ Adding straps attached 
- ❌ Adding wrist weights
- ❌ Adding resistance bands
- ❌ Adding equipment "because the exercise usually uses it" - ONLY copy whats stated in the description

✅ If the description shows BARE LEGS with plain shoes → YOUR OUTPUT: bare legs with plain shoes
✅ If the sescription shows NO equipment on the body → YOUR OUTPUT: no equipment on the body

DO NOT assume equipment should be there. ONLY include what is CLEARLY STATED in the reference image.

🔌 CABLES/BANDS GOING OFF-FRAME - CRITICAL:
============================================
- If the description mentions resistance band/cable:
  * The cable MUST exit the frame edge naturally (not cut off mid-air)
  * Extend the cable all the way to the edge of your generated image
  * DO NOT show what it's attached to (door, wall, anchor - leave that off-frame)
  * DO NOT let the cable end awkwardly in mid-air inside the frame
  
- EXAMPLE: If description mentions a person holding resistance band cable:
  ✅ CORRECT: Cable extends from their hands all the way to frame edge (left/right)
  ❌ WRONG: Cable ends floating in mid-air inside the frame
  ❌ WRONG: Showing door/wall anchor point that wasn't in reference
  
- Position the model close enough to frame edge that the cable can exit naturally
- Match the cable's angle/direction from reference, but EXTEND it to frame edge

🏗️ STRUCTURAL EQUIPMENT (POLES/BARS) - COMPLETE THEM:
======================================================
- If the description mentions VERTICAL POLES, BARS, or STRUCTURAL SUPPORTS:
  * These are different from cables - SHOW THEM COMPLETELY
  * Extend vertical poles from FLOOR TO CEILING (or top of frame)
  * DO NOT show half a pole - complete the structure
  
- EXAMPLES:
  * Pull-up bar → Show full vertical supports + top bar
  * TRX/suspension trainer pole → Show full floor-to-ceiling pole
  * Ballet barre → Show full vertical supports
  * Parallel bars → Show complete structure
  
- ✅ CORRECT: Full pole/bar structure visible (floor to ceiling or frame edge to frame edge)
- ❌ WRONG: Half a pole that ends mid-air awkwardly
- ❌ WRONG: Incomplete structural supports

- The difference: CABLES can exit frame (we don't see anchor), but POLES/BARS should be complete

🧘 FLOOR/MAT CONTACT - CRITICAL:
=================================
- If the description mentions ANY of these positions, person MUST be ON THE FLOOR/MAT:
  * Supine (lying on back)
  * Prone (lying face down)
  * Side-lying
  * Sitting on floor
  * Kneeling positions
  * Any "lying" or "floor" exercise
  
- ❌ WRONG: Person floating in mid-air with no floor contact
- ✅ CORRECT: Person clearly resting on floor/mat with proper contact points
- Add a yoga mat or show the wood floor under them
- Ensure body parts that should touch floor (back, legs, etc.) are actually ON the surface
- DO NOT generate floating/levitating people just because description is unclear
`;

export const CONTEXT_SHARED = `GENERATE A NEW PERSON:
======================
- White female fitness professional (30-40 years old)
- Caucasian with fair to light skin tone
- Professional, calm, focused expression
- Athletic but not overly muscular build
- Hair: Dark brown or black, pulled back in neat bun or ponytail
- Professional fitness instructor appearance
- CRITICAL: Generate EXACTLY TWO ARMS and TWO LEGS - normal human anatomy
- NO extra limbs, NO deformed body parts
- Clean, anatomically correct human figure

GENERATE NEW STUDIO SETTING:
============================
- Background: Clean beige/cream wall (smooth, matte)
- Floor: Light natural wood (horizontal planks, warm honey/oak tone)
- Optional navy blue yoga mat if needed
- Professional medical/fitness studio aesthetic

GENERATE NEW CLOTHING:
======================
- TOP: Navy blue athletic t-shirt (short sleeves, fitted)
- BOTTOM: Black athletic leggings (full length, fitted)
- FOOTWEAR: Match the reference image EXACTLY:
  * If reference shows BARE FEET → generate bare feet (no shoes, no socks)
  * If reference shows SHOES → generate white or cream athletic shoes
  * If reference shows SOCKS ONLY → generate white athletic socks (no shoes)
- Clean, professional appearance

LIGHTING:
=========
- Soft professional studio lighting
- Even, diffused (no harsh shadows)
- Professional photography quality

IMAGE SPECIFICATIONS:
=====================
🎯 MANDATORY OUTPUT FORMAT:
- EXACT Resolution: 1920 pixels wide × 1080 pixels tall
- Aspect ratio: 16:9 (widescreen/landscape)
- Orientation: HORIZONTAL/LANDSCAPE (wide, not tall)
- The image MUST be wider than it is tall
- High quality, professional photography
- Sharp focus on the person and exercise
- Dont add any text in the image

🚨 CRITICAL REQUIREMENTS:
=========================
1. OUTPUT SIZE: The final image MUST be exactly 1920x1080 pixels (Full HD landscape format)
2. This is medical documentation - pose biomechanics MUST be pixel-perfect
3. Include ALL equipment from reference image (don't skip chairs, props, etc.)
4. The person and setting should be completely new - do NOT copy the person from reference
5. LANDSCAPE orientation - image should be WIDE (1920px) not TALL (1080px)
6. Never output text or instructions in the image, when the term instructions, descriptions, title, name etc is used in the prompt its only meant to give context, not to be outputted in the image.

⚠️ DIMENSION ENFORCEMENT:
==========================
WIDTH: 1920 pixels (EXACTLY)
HEIGHT: 1080 pixels (EXACTLY)
DO NOT generate any other size. The output MUST be 1920x1080.
This is a HARD REQUIREMENT - no exceptions.

🎨 QUALITY REQUIREMENTS:
========================
- 4K HDR QUALITY: Ultra-high-definition professional photography
- MAXIMUM RESOLUTION: Generate at 1920x1080 or higher
- 16:9 ASPECT RATIO: Widescreen landscape format
- ULTRA HIGH QUALITY: Professional photography standard
- SHARP DETAILS: Crystal clear focus, no blur, maximum detail
- The person should fill the frame similar to the reference image
- Match the scale and composition of the reference exactly
- NO anatomical errors (correct number of limbs, proper body proportions)
- NO extra limbs, NO deformed body parts, NO blurry areas
`;

export const generatePromptInstructions = (hasReferenceImage: boolean, prompt: string, disableContext: boolean) => {
  if (hasReferenceImage) {
    return disableContext ? prompt : generateFixPrompt(prompt) + CONTEXT_WITH_REF_IMAGE + CONTEXT_SHARED;
  } else {
    return disableContext ? prompt : generateFixPrompt(prompt) + CONTEXT_WITHOUT_REF_IMAGE + CONTEXT_SHARED;
  }
};
